import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';

const SITE_URL = 'https://luosai123.cn';

const principles = [
  {
    icon: 'ri-shield-check-line',
    title: '只做导航，不做裁判',
    desc: '我们负责把入口按类别整理清楚，不对第三方平台的单条内容下结论，也不替任何一方站台。',
  },
  {
    icon: 'ri-eye-line',
    title: '标注核验时间',
    desc: '每个入口都记录最近一次可访问性核验日期。无法访问或方向变化的条目会及时下架，而不是长期挂着。',
  },
  {
    icon: 'ri-lock-2-line',
    title: '隐私优先',
    desc: '指南与提醒中反复强调隐去非必要个人信息。我们不会鼓励把他人隐私当作传播筹码。',
  },
  {
    icon: 'ri-scales-line',
    title: '不煽动、不夸大',
    desc: '描述渠道时说明它的规则与门槛，不制造紧迫感，也不使用情绪化的标题来换取点击。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续维护',
    desc: '互联网上的入口变化很快，我们按月做一次集中清理，也欢迎用户通过反馈帮我们发现失效链接。',
  },
];

const workflow = [
  {
    title: '收集与登记',
    desc: '从公开渠道收集候选入口，登记其类别、定位与基本规则。',
  },
  {
    title: '可访问性核验',
    desc: '逐条打开确认可以正常访问，检查发布规则是否清晰、是否存在明显收费陷阱。',
  },
  {
    title: '分类与标注',
    desc: '归入合适的类别，标注核验日期与材料要求，再决定是否标记"已核验"。',
  },
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们',
        url: `${SITE_URL}/about`,
        description: '爆料网上是一个爆料平台与论坛社区导航门户，介绍其定位、内容原则与核验流程。',
        inLanguage: 'zh-CN',
        mainEntity: {
          '@type': 'Organization',
          name: '爆料网上',
          url: `${SITE_URL}/`,
          email: 'hello@baoliao-online.example.com',
          description: '聚合全网热门爆料网站与论坛社区的导航门户。',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: `${SITE_URL}/about` },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: '关于我们 - 导航定位与内容原则｜爆料网上',
    description:
      '了解爆料网上：一个把爆料网站与论坛社区整理成清晰目录的导航门户。这里说明我们的定位、内容原则、核验流程与联系方式，以及我们不做哪些事情。',
    keywords: '关于爆料网上,爆料平台导航,内容原则,核验流程,爆料网上',
    path: '/about',
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            爆料网上是一个内容导航站，不是爆料平台本身。我们相信，找到对的入口，
            比盲目地到处发布更重要，也更安全。
          </p>
        </div>
      </div>

      <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <p className="text-xs font-medium tracking-[0.18em] text-primary-600">我们的起点</p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              从"到处乱投"到"找对一处"
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
              <p>
                最初做这个导航，是因为身边太多人遇到问题后第一反应是"到处发一遍"。结果要么发到了完全不对的版块，
                要么把关键材料裁得只剩一句话，最后石沉大海，还白白暴露了自己的信息。
              </p>
              <p>
                我们想做的事情很朴素：把分散在各处的爆料入口按类别整理成一份可检索的目录，
                再配套一套讲得清楚、可执行的投稿指南。让你在动手之前就知道该去哪里、要准备什么、需要注意什么。
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/directory"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                浏览平台导航<i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/guide"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                阅读发布指南
              </Link>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-100 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20editorial%20studio%20desk%20with%20open%20notebooks%2C%20folded%20newspapers%20and%20a%20laptop%2C%20cream%20walls%20and%20light%20wooden%20surface%2C%20soft%20natural%20daylight%2C%20clean%20organized%20composition%2C%20top%20down%20view%2C%20no%20text&width=1200&height=900&seq=baoliao-about-studio&orientation=landscape"
                alt="爆料网上编辑工作台，摆放着笔记本、报纸与电脑"
                title="关于爆料网上 - 内容导航与整理工作"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">网站定位：做什么，不做什么</h2>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-secondary-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-secondary-800">
                <i className="ri-checkbox-circle-line"></i>我们会做的
              </h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
                {[
                  '按类别整理爆料平台、发布入口与论坛社区',
                  '标注核验时间、开放方式与材料要求',
                  '整理从材料准备到投递跟进的流程指南',
                  '提醒隐私保护与事实核查的注意事项',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <i className="ri-check-line mt-0.5 text-secondary-500"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-800">
                <i className="ri-close-circle-line"></i>我们不会做的
              </h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
                {[
                  '不直接受理线索，也不代发任何内容',
                  '不对第三方平台的单条内容作真实性结论',
                  '不提供付费的"删帖""公关"或"加急"服务',
                  '不鼓励公开他人隐私或进行人身攻击',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <i className="ri-subtract-line mt-0.5 text-foreground-400"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="principles" className="scroll-mt-28 border-y border-background-200 bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">内容原则</h2>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这些原则决定了我们收录什么、拒绝什么，也决定了这份目录长期可信的基础。
          </p>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {principles.map((item, index) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <span className="font-heading text-2xl font-bold text-background-300">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">我们如何核验一个入口</h2>
          <ol className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {workflow.map((step, index) => (
              <li key={step.title} className="relative rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 font-heading text-sm font-bold text-primary-950">
                  {index + 1}
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{step.desc}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 flex items-start gap-2 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-600">
            <i className="ri-information-line mt-0.5 text-accent-600"></i>
            需要说明的是，"已核验"只代表我们确认该入口在最近一次检查中可以正常访问，
            并不代表我们对其中的任何单条内容作出真实性判断。请始终自行核实后再决定是否采信或传播。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
        <div className="rounded-lg bg-secondary-800 px-6 py-10 md:px-12 md:py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">有建议或需要补充渠道？</h2>
              <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
                反馈入口失效、补充新的平台、指出内容误差，或者只是想聊聊你的情况，都可以通过联系我们页面找到我们。
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm text-background-100">
                <i className="ri-mail-line text-accent-300"></i>
                hello@baoliao-online.example.com
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>前往联系我们
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}